import React from 'react';
import MaterialIcon from '@/components/MaterialIcon';
import SmartLink from '@/components/SmartLink';
import { useTracking } from '@mygpt/app/utils';

/**
 * HeroSection組件
 * Solution页面英雄区域
 *
 * 左侧：主标题、描述、CTA按钮
 * 右侧：Live Demo 10种语言选择器 - 点击跳转到kiosk-floating页面
 */
export const HeroSection: React.FC = () => {
  const { trackSignupClick } = useTracking();
  const languages = [
    { code: 'ja', flag: '🇯🇵', name: '日本語', url: 'kiosk-floating.html?lang=ja' },
    { code: 'en', flag: '🇺🇸', name: 'English', url: 'kiosk-floating.html?lang=en' },
    { code: 'zh', flag: '🇨🇳', name: '中文', url: 'kiosk-floating.html?lang=zh' },
    { code: 'ko', flag: '🇰🇷', name: '한국어', url: 'kiosk-floating.html?lang=ko' },
    { code: 'vi', flag: '🇻🇳', name: 'Tiếng Việt', url: 'kiosk-floating.html?lang=vi' },
    { code: 'pt', flag: '🇵🇹', name: 'Português', url: 'kiosk-floating.html?lang=pt' },
    { code: 'pt-br', flag: '🇧🇷', name: 'Português BR', url: 'kiosk-floating.html?lang=pt-br' },
    { code: 'id', flag: '🇮🇩', name: 'Indonesia', url: 'kiosk-floating.html?lang=id' },
    { code: 'ne', flag: '🇳🇵', name: 'नेपाली', url: 'kiosk-floating.html?lang=ne' },
    { code: 'es', flag: '🇪🇸', name: 'Español', url: 'kiosk-floating.html?lang=es' },
  ];

  return (
    <div className="w-full px-4 md:px-10 lg:px-40 py-16 md:py-24 flex justify-center bg-white">
      <div className="max-w-[1200px] w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* 左侧：标题和CTA */}
        <div className="flex flex-col gap-6 text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-solution-orange/10 w-fit">
            <MaterialIcon name="smart_toy" className="text-solution-orange text-sm" />
            <span className="text-solution-orange text-xs font-bold uppercase tracking-wider">
              商業施設・百貨店向けAIチャットボット - フロアマップ連携で案内対応を自動化
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-5xl font-black leading-tight tracking-[-0.033em] text-solution-text">
            案内所の問い合わせを
            <br />
            <span className="text-solution-orange">50~70%削減</span>
          </h1>
          <p className="text-lg text-solution-text-secondary leading-relaxed max-w-xl">
            <strong>AIコンシェルジュ</strong>が24時間365日、お客様の質問に自動対応。
            <br />
            フロアマップと連携した高度なチャットボットで、
            <br />
            案内業務の負担を大幅削減。
          </p>
          <p className="text-lg text-solution-text-secondary leading-relaxed max-w-xl">
            最短
            <strong>1週間</strong>で導入、専用端末<strong>不要</strong>。
            <br />
            店舗検索から在庫確認、多言語対応まで。
            <br />
            大型商業施設で実証済みのAIコンシェルジュを、あなたの施設にも。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-2">
            <SmartLink
              to="/register"
              className="flex items-center justify-center rounded-lg h-12 px-8 bg-solution-orange hover:bg-solution-orange-dark transition-colors text-white text-base font-bold shadow-lg shadow-solution-orange/20"
              onClick={() => trackSignupClick('hero')}
            >
              14日間無料トライアル開始
            </SmartLink>
            {/* Hidden temporarily - will be moved to case study page
            <SmartLink
              href="https://cs.gbase.ai/download"
              target="_blank"
              className="flex items-center justify-center rounded-lg h-12 px-8 bg-white border border-solution-border hover:border-solution-orange transition-colors text-solution-text text-base font-bold"
            >
              導入事例PDFを入手
            </SmartLink>
            */}
          </div>
        </div>

        {/* 右侧：Language Selector */}
        <div className="relative w-full">
          <div className="bg-white rounded-2xl shadow-2xl border border-solution-border p-6 md:p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <p className="text-lg font-bold text-solution-text mb-1">Live Demo</p>
              <p className="text-sm text-solution-text-secondary">言語を選択して体験</p>
            </div>
            {/* Language Grid */}
            <div className="grid grid-cols-5 gap-3 md:gap-4">
              {languages.map((lang) => (
                <a
                  key={lang.code}
                  href={lang.url}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-solution-orange/5 hover:scale-105 transition-all cursor-pointer group"
                >
                  <span className="text-3xl md:text-4xl">{lang.flag}</span>
                  <span className="text-xs font-medium text-solution-text-secondary group-hover:text-solution-orange">
                    {lang.name}
                  </span>
                </a>
              ))}
            </div>
            {/* Hint */}
            <div className="mt-6 text-center">
              <p className="text-xs text-solution-text-secondary flex items-center justify-center gap-1">
                <MaterialIcon name="touch_app" className="text-solution-orange text-sm" />
                国旗をクリックしてAIコンシェルジュを体験
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
